import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Award, Loader2, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiGet, apiPost } from '@/lib/api';

const VERDICT_TONE = {
  paid_off: 'border-emerald-200 bg-emerald-50/70 text-emerald-900',
  lucky: 'border-amber-200 bg-amber-50/70 text-amber-900',
  plan_too_light: 'border-sky-200 bg-sky-50/70 text-sky-900',
  needs_more_time: 'border-rose-200 bg-rose-50/70 text-rose-900',
};

/** Marks in → an honest read on whether the PLAN worked (never on the child). */
export function ExamResultDialog({ exam, open, onOpenChange }) {
  const [insight, setInsight] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !exam) return;
    apiGet(`/premium/v1/exams/${exam.exam_id}/results`).then(d => {
      setInsight(d);
      setRows((d.exam_subjects || []).map(s => {
        const found = (d.subjects || []).find(x => x.subject === s);
        return { subject: s, marks_obtained: found?.marks_obtained ?? '', marks_total: found?.marks_total ?? '' };
      }));
    }).catch(() => setInsight(null));
  }, [open, exam]);

  const save = async () => {
    const payload = rows
      .filter(r => r.marks_obtained !== '' && r.marks_total !== '')
      .map(r => ({ subject: r.subject, marks_obtained: Number(r.marks_obtained), marks_total: Number(r.marks_total) }));
    if (!payload.length) { toast.error('Add the marks for at least one subject'); return; }
    setBusy(true);
    try {
      setInsight(await apiPost(`/premium/v1/exams/${exam.exam_id}/results`, { subjects: payload }));
      toast.success('Saved — here is what the plan actually delivered');
    } catch (e) { toast.error(e?.message || 'Could not save those marks'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="exam-result-dialog">
        <DialogTitle className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
          <Award className="w-4 h-4 text-amber-500" /> How did {exam?.name} go?
        </DialogTitle>
        <DialogDescription className="text-xs text-slate-500 font-body">
          Enter the marks and we'll show which planned chapters actually paid off.
        </DialogDescription>

        {!insight && <p className="py-6 text-center text-sm text-slate-400 font-body"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>}

        {insight && (
          <div className="space-y-3">
            <div className="space-y-2" data-testid="result-marks-form">
              {rows.map((r, i) => (
                <div key={r.subject} className="flex items-center gap-2">
                  <p className="text-[12px] font-body text-slate-700 flex-1 truncate">{r.subject}</p>
                  <Input data-testid={`result-obtained-${r.subject}`} type="number" placeholder="Scored"
                    value={r.marks_obtained} className="h-8 w-24 rounded-lg text-sm"
                    onChange={e => setRows(v => v.map((x, j) => (j === i ? { ...x, marks_obtained: e.target.value } : x)))} />
                  <span className="text-slate-400 text-xs">/</span>
                  <Input data-testid={`result-total-${r.subject}`} type="number" placeholder="Out of"
                    value={r.marks_total} className="h-8 w-24 rounded-lg text-sm"
                    onChange={e => setRows(v => v.map((x, j) => (j === i ? { ...x, marks_total: e.target.value } : x)))} />
                </div>
              ))}
              <Button size="sm" className="h-8 text-[11px] bg-slate-900 text-white" disabled={busy}
                data-testid="result-save-btn" onClick={save}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Save marks
              </Button>
            </div>

            {insight.has_results && (
              <div className="space-y-2" data-testid="result-insight">
                {insight.overall_percent !== null && (
                  <p className="text-[12px] font-heading text-slate-800 flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-indigo-500" /> Overall {insight.overall_percent}%
                  </p>
                )}
                {insight.subjects.map(s => (
                  <div key={s.subject} className={`rounded-2xl border p-3 ${VERDICT_TONE[s.verdict]}`}
                    data-testid={`result-verdict-${s.subject}`}>
                    <p className="font-heading text-[12px] font-semibold">
                      {s.subject} · {s.percent}%{s.coverage_pct !== null ? ` · plan ${s.coverage_pct}% done` : ''}
                    </p>
                    <p className="text-[11px] font-body mt-0.5 leading-relaxed">{s.message}</p>
                    {s.chapters_paid_off?.length > 0 && (
                      <p className="text-[10px] font-body mt-1">Paid off: {s.chapters_paid_off.join(', ')}</p>
                    )}
                    {s.chapters_to_revisit?.length > 0 && (
                      <p className="text-[10px] font-body mt-1">Revisit: {s.chapters_to_revisit.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ExamResultDialog;
